// src/pages/ProductList.jsx
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, Link } from "react-router-dom";
import { getProducts } from "../services/productService";
import { useCart } from "../context/CartContext";
import {
  LuMinus,
  LuPlus,
  LuShoppingCart,
  LuArrowLeft,
  LuSearch,
  LuX,
  LuChevronLeft,
  LuChevronRight,
  LuArrowRight,
} from "react-icons/lu";
import "./ProductList.css";

// Placeholder image for items without images
const PLACEHOLDER_IMAGE =
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop";

export default function ProductList() {
  const { restaurantId } = useParams();

  // State management
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs for category scroll
  const categoryScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Ref to prevent duplicate API calls
  const fetchedRef = useRef(false);

  const {
    addToCart,
    removeFromCart,
    updateQty,
    getItemCount,
    isInCart,
    getItemQty,
    setRestaurantSettings,
  } = useCart();

  const cartCount = getItemCount();

  // Reset fetch flag when restaurantId changes
  useEffect(() => {
    fetchedRef.current = false;
  }, [restaurantId]);

  // Fetch menu items on mount
  useEffect(() => {
    if (fetchedRef.current) return;

    const fetchMenuItems = async () => {
      try {
        setLoading(true);

        // Fetch products
        const response = await getProducts(restaurantId);

        const products = response.products || [];
        const restaurantSettings = response.restaurant || {};

        console.log("restaurantSettings:", restaurantSettings);

        // Fetch restaurant settings
        setRestaurantSettings(restaurantSettings);

        setItems(products);
        setFilteredItems(products);
        setError(null);
      } catch (err) {
        setError("Session Expired. Start ordering by saying HI from whatsapp.");
        console.error("Error fetching menu:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
    fetchedRef.current = true;
  }, [restaurantId, setRestaurantSettings]);

  // Filter items: restaurantId -> category -> search
  // Also sort to show special's only for you category products first
  useEffect(() => {
    const searchLower = searchTerm.toLowerCase().trim();

    // If no search → behave normally
    if (!searchLower) {
      if (selectedCategory === "all") {
        // Sort to show special's only for you products first
        const sortedItems = [...items].sort((a, b) => {
          const aIsSpecial = a.category === "special's only for you" ? 0 : 1;
          const bIsSpecial = b.category === "special's only for you" ? 0 : 1;
          return aIsSpecial - bIsSpecial;
        });
        setFilteredItems(sortedItems);
      } else {
        setFilteredItems(
          items.filter((item) => item.category === selectedCategory)
        );
      }
      return;
    }

    // 🔍 Global Search Results
    const globalResults = items.filter((item) => {
      return (
        item.item_name?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower) ||
        item.subcategory?.toLowerCase().includes(searchLower)
      );
    });

    // If category is ALL → just show global results
    if (selectedCategory === "all") {
      // Sort to show special's only for you products first
      const sortedResults = [...globalResults].sort((a, b) => {
        const aIsSpecial = a.category === "special's only for you" ? 0 : 1;
        const bIsSpecial = b.category === "special's only for you" ? 0 : 1;
        return aIsSpecial - bIsSpecial;
      });
      setFilteredItems(sortedResults);
      return;
    }

    // 📂 Category Filtered Results
    const categoryResults = globalResults.filter(
      (item) => item.category === selectedCategory
    );

    // 🔀 Merge category results + other global matches
    const mergedResults = [
      ...categoryResults,
      ...globalResults.filter(
        (item) =>
          item.category !== selectedCategory &&
          !categoryResults.some((c) => c.item_id === item.item_id)
      ),
    ];

    setFilteredItems(mergedResults);
  }, [items, selectedCategory, searchTerm]);
  // useEffect(() => {
  //   let result = [...items];

  //   // Filter by category
  //   if (selectedCategory !== "all") {
  //     result = result.filter((item) => item.category === selectedCategory);
  //   }

  //   // Filter by search term
  //   if (searchTerm.trim()) {
  //     const searchLower = searchTerm.toLowerCase().trim();
  //     result = result.filter((item) => {
  //       const nameMatch = item.item_name?.toLowerCase().includes(searchLower);
  //       const categoryMatch = item.category
  //         ?.toLowerCase()
  //         .includes(searchLower);
  //       const subcategoryMatch = item.subcategory
  //         ?.toLowerCase()
  //         .includes(searchLower);
  //       return nameMatch || categoryMatch || subcategoryMatch;
  //     });
  //   }

  //   setFilteredItems(result);
  // }, [items, selectedCategory, searchTerm]);

  // Get unique categories from items
  // special's only for you category should appear first (after "all")
  const categories = useMemo(() => {
    const uniqueCategories = new Set(
      items.map((item) => item.category).filter(Boolean)
    );
    const sorted = Array.from(uniqueCategories).sort();
    // Move special's only for you to the front (after "all")
    const specialIndex = sorted.indexOf("special's only for you");
    if (specialIndex > -1) {
      sorted.splice(specialIndex, 1);
      sorted.unshift("special's only for you");
    }
    return ["all", ...sorted];
  }, [items]);

  // Check scroll position for navigation buttons
  const checkScrollPosition = useCallback(() => {
    if (categoryScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        categoryScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  }, []);

  // Scroll category bar
  const scrollCategory = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      categoryScrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Handle category click with scroll into view
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    // Scroll to center the selected category
    if (categoryScrollRef.current) {
      const button = categoryScrollRef.current.querySelector(
        `button[data-category="${category}"]`
      );
      if (button) {
        button.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }
  };

  // Check if item is in stock (instock === 1 means in stock)
  const isInStock = (item) => {
    return item.instock === 1;
  };

  // Get product image URL
  const getProductImage = (item) => {
    if (item.images && item.images.length > 0 && item.images[0]) {
      return item.images[0];
    }
    return PLACEHOLDER_IMAGE;
  };

  // Handle add to cart
  const handleAddToCart = (product) => {
    if (isInStock(product)) {
      addToCart(product);
    }
  };

  // Handle quantity change
  const handleQtyChange = (itemId, delta) => {
    const currentQty = getItemQty(itemId);
    const newQty = currentQty + delta;

    if (newQty <= 0) {
      removeFromCart(itemId);
    } else {
      updateQty(itemId, newQty);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  if (loading) {
    return (
      <div className="product-list-container">
        <div className="product-list-loading">
          <div className="loading-spinner"></div>
          <p>Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-list-container">
        <div className="product-list-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-list-container">
      {/* Header */}
      <div className="product-list-header">
        <Link to="/home" className="back-link">
          <LuArrowLeft size={20} />
        </Link>
        <h1>Products Menu</h1>
        {/* <Link to="/cart" className="cart-link">
          <LuShoppingCart size={24} />
          {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
        </Link> */}
      </div>

      {/* Illustration Note */}
      <div className="photo-note">
        ℹ️ Photos are for illustration purposes only.
      </div>

      {/* Search Bar */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <LuSearch className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, category, or subcategory..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <button className="search-clear" onClick={clearSearch}>
              <LuX size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Category Filter - Horizontal Scrollable with Sticky */}
      <div className="category-section">
        {/* Scroll Left Button */}
        {canScrollLeft && (
          <button
            className="category-scroll-btn category-scroll-left"
            onClick={() => scrollCategory("left")}
            aria-label="Scroll categories left"
          >
            <LuChevronLeft size={20} />
          </button>
        )}

        {/* Category Bar - Scrollable */}
        <div
          className="category-filter"
          ref={categoryScrollRef}
          onScroll={checkScrollPosition}
        >
          {categories.map((category) => (
            <button
              key={category}
              data-category={category}
              className={`category-btn ${
                selectedCategory === category ? "active" : ""
              }`}
              onClick={() => handleCategoryClick(category)}
            >
              {category === "all" ? "All" : category}
            </button>
          ))}
        </div>

        {/* Scroll Right Button */}
        {canScrollRight && (
          <button
            className="category-scroll-btn category-scroll-right"
            onClick={() => scrollCategory("right")}
            aria-label="Scroll categories right"
          >
            <LuChevronRight size={20} />
          </button>
        )}
      </div>

      {/* Products Grid */}
      <div className="products-grid">
        {filteredItems.map((item) => {
          const inCart = isInCart(item.item_id);
          const itemQty = getItemQty(item.item_id);
          const inStock = isInStock(item);

          return (
            <div
              key={item.item_id}
              className={`product-card ${!inStock ? "out-of-stock" : ""}`}
            >
              <div className="product-image">
                <img
                  src={getProductImage(item)}
                  alt={item.item_name}
                  loading="lazy"
                />
                {!inStock && (
                  <div className="unavailable-overlay">Out of Stock</div>
                )}
              </div>

              <div className="product-info">
                <h3 className="product-name">{item.item_name}</h3>
                <p className="product-category">{item.category}</p>
                <div className="product-price-qty">
                  <div className="price-container">
                    <span className="product-price">₹{item.price}</span>
                  </div>

                  {inStock ? (
                    inCart ? (
                      <div className="qty-controls">
                        <button
                          className="qty-btn"
                          onClick={() => handleQtyChange(item.item_id, -1)}
                        >
                          <LuMinus size={16} />
                        </button>
                        <span className="qty-value">{itemQty}</span>
                        <button
                          className="qty-btn"
                          onClick={() => handleQtyChange(item.item_id, 1)}
                        >
                          <LuPlus size={16} />
                        </button>
                      </div>
                    ) : (
                      <button
                        className="add-btn"
                        onClick={() => handleAddToCart(item)}
                      >
                        Add
                      </button>
                    )
                  ) : (
                    <button className="add-btn" disabled>
                      Out of Stock
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="no-products">
          <p>No products found.</p>
        </div>
      )}

      {/* Sticky Bottom Cart Bar - Premium Style */}
      {cartCount > 0 && (
        <div
          className="sticky-cart-bar"
          onClick={() => (window.location.href = "/cart")}
        >
          <div className="cart-icon-wrapper">
            <LuShoppingCart />
          </div>
          <div className="cart-text">
            <span className="cart-title">View Cart ({cartCount} items)</span>
            <span className="cart-subtitle">Tap to checkout</span>
          </div>
          <div className="cart-arrow">
            <LuArrowRight />
          </div>
        </div>
      )}
    </div>
  );
}
